import Box from '@mui/joy/Box'
import Breadcrumbs from '@mui/joy/Breadcrumbs'
import CssBaseline from '@mui/joy/CssBaseline'
import Link from '@mui/joy/Link'
import Typography from '@mui/joy/Typography'
import { CssVarsProvider, useColorScheme } from '@mui/joy/styles'
import { extendTheme as materialExtendTheme, CssVarsProvider as MaterialCssVarsProvider, THEME_ID as MATERIAL_THEME_ID } from '@mui/material/styles'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import { Children } from 'types'
import { useLocation, useNavigate } from 'react-router-dom'

// Inner wrapper — must be inside CssVarsProvider to call useColorScheme.
// Sets data-color-mode so third-party components (e.g. MDEditor) inherit the
// active Joy theme without each component needing its own useColorScheme call.
function ThemeRoot({ children }: Children) {
    const { mode, systemMode } = useColorScheme()
    const colorMode = (mode === 'system' ? systemMode : mode) ?? 'light'
    return (
        <Box data-color-mode={colorMode} className={colorMode === 'dark' ? 'dark' : ''} sx={{ display: 'flex', minHeight: '100dvh' }}>
            {children}
        </Box>
    )
}

export default function MasterLayout({ children }: Children) {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const pathnameList = pathname.split('/').filter(Boolean)

    const materialTheme = materialExtendTheme()

    return (
        <MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
            <CssVarsProvider disableTransitionOnChange>
                <CssBaseline />
                <ThemeRoot>
                    <Header />
                    <Sidebar />
                    <Box
                        component="main"
                        className="MainContent"
                        sx={{
                            px: { xs: 2, md: 6 },
                            pt: {
                                xs: 'calc(12px + var(--Header-height))',
                                sm: 'calc(12px + var(--Header-height))',
                                md: 3,
                            },
                            pb: { xs: 2, sm: 2, md: 3 },
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                            height: '100dvh',
                            gap: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon fontSize="small" />} sx={{ pl: 0 }}>
                                <Link underline="none" color="neutral" onClick={() => navigate('/')}>
                                    <HomeRoundedIcon />
                                </Link>
                                {pathnameList.map((path, index) => {
                                    return index === pathnameList.length - 1 ? (
                                        <Typography key={index} color="neutral" fontWeight={500} fontSize={12} sx={{ textTransform: 'capitalize' }}>
                                            {path}
                                        </Typography>
                                    ) : (
                                        <Link
                                            key={index}
                                            underline="none"
                                            color="neutral"
                                            onClick={() => navigate(`/${pathnameList.slice(0, index + 1).join('/')}`)}
                                        >
                                            <Typography color="primary" fontWeight={500} fontSize={12} sx={{ textTransform: 'capitalize' }}>
                                                {path}
                                            </Typography>
                                        </Link>
                                    )
                                })}
                            </Breadcrumbs>
                        </Box>

                        {children}
                    </Box>
                </ThemeRoot>
            </CssVarsProvider>
        </MaterialCssVarsProvider>
    )
}
